// Event catalog (04 §7.4). Payload schemas are added as each event is implemented.
import { z } from 'zod';
import { facilityTypeSchema, idSchema, isoDateTimeSchema, shipTypeSchema } from './common.js';

export const EVENT_NAMES = [
  'SYSTEM_STATE_UPDATED',
  'BUILDING_COMPLETED',
  'RESEARCH_COMPLETED',
  'SHIP_PRODUCED',
  'PORT_TRADE_COMPLETED',
  'TRADE_OFFER_RECEIVED',
  'TRADE_OFFER_ACCEPTED',
  'TRADE_OFFER_DECLINED',
  'TRADE_OFFER_DELIVERED',
  'FLEET_DEPARTED',
  'FLEET_ARRIVED',
  'FLEET_RETURNED',
  'FLEET_EJECTED',
  'FLEET_INCOMING',
  'FLEET_INCOMING_CANCELLED',
  'FLEET_ORDERS_CHANGED',
  'SCOUT_RETURNED',
  'SCOUT_FAILED_AGAINST_YOU',
  'INTEL_REPORT_SHARED',
  'MISSION_RETURNED',
  'BATTLE_STARTED',
  'BATTLE_ROUND_RESOLVED',
  'BATTLE_FLEET_JOINED',
  'BATTLE_RETREAT_ORDERED',
  'BATTLE_ENDED',
  'BATTLE_WAITING_CHANGED',
  'CLAIM_PLACED',
  'CLAIM_ACTIVATED',
  'CLAIM_RESOLVED',
  'SYSTEM_OWNER_CHANGED',
  'CITADEL_FORMED',
  'MAP_CHANGED',
  'PLAYER_SHIELD_CHANGED',
  'SHIELD_WARNING',
  'SHIELD_EXPIRED',
  'ALLIANCE_MEMBERSHIP_CHANGED',
  'ALLIANCE_UPDATED',
  'ALLIANCE_DISBANDED',
  'ALLIANCE_INVITE_RECEIVED',
  'ALLIANCE_MOTD_CHANGED',
  'ALLIANCE_ATTACKS_CHANGED',
  'DIPLOMACY_PROPOSAL_CHANGED',
  'DIPLOMACY_STATUS_CHANGED',
  'WAR_DECLARED',
  'WAR_STARTED',
  'WAR_ENDED',
  'WAR_SCORE_CHANGED',
  'GALAXY_PEACE_DECLARED',
  'GALAXY_PEACE_BROKEN',
  'GALAXY_BROADCAST',
  'CHAT_MESSAGE',
  'CHAT_MESSAGE_REMOVED',
  'NOTIFICATION_CREATED',
  'MILESTONE_ACHIEVED',
  'DARK_MATTER_CHANGED',
  'COMMANDER_PASS_CHANGED',
] as const;
export type EventName = (typeof EVENT_NAMES)[number];

export const SYSTEM_STATE_REASONS = [
  'building_started',
  'building_completed',
  'building_cancelled',
  'port_trade_started',
  'port_trade_completed',
  'ship_production_started',
  'ship_produced',
  'ship_production_cancelled',
  'fleets_changed',
  'owner_changed',
  /** Stored Influence changed outside production: a scout sent, or a failed scout against it. */
  'influence_changed',
  /** A claim on the system was placed, activated or resolved (M3). */
  'claims_changed',
  /** The owner renamed the system or restored its generated name (G-60). */
  'renamed',
] as const;

export const shipProducedSchema = z.strictObject({
  systemId: idSchema,
  itemId: idSchema,
  shipType: shipTypeSchema,
  delivered: z.number().int().positive(),
  quantity: z.number().int().positive(),
});

/** `system:` and the owner's `player:` channel. `changes` holds the parts of the full view that changed. */
export const systemStateUpdatedSchema = z.strictObject({
  systemId: idSchema,
  reason: z.enum(SYSTEM_STATE_REASONS),
  changes: z.record(z.string(), z.unknown()),
});

export const buildingCompletedSchema = z.strictObject({
  systemId: idSchema,
  facilityType: facilityTypeSchema,
  level: z.number().int().positive(),
});

export const portTradeCompletedSchema = z.strictObject({
  systemId: idSchema,
  tradeId: idSchema,
  creditsOut: z.number().int().nonnegative(),
  credits: z.number().int().nonnegative(),
});

export const systemOwnerChangedPublicSchema = z.strictObject({
  systemId: idSchema,
  ownerPlayerId: idSchema.nullable(),
  ownerAllianceId: idSchema.nullable(),
});

export const mapChangedSchema = z.strictObject({ version: z.string() });

export const playerShieldChangedSchema = z.strictObject({
  playerId: idSchema,
  shielded: z.boolean(),
});

export const shieldTimerEventSchema = z.strictObject({ shieldUntil: isoDateTimeSchema });

export const notificationCreatedSchema = z.strictObject({
  notificationId: idSchema,
  type: z.string(),
  unreadCount: z.number().int().nonnegative(),
});
